import { AttemptState } from '../enums/AttemptState';

export enum CallsStateType {
  'Unknown' = 'Unknown',
  'ErrorTimeout' = 'ErrorTimeout',
  'ErrorBusy' = 'ErrorBusy',
  'ErrorForbidden' = 'ErrorForbidden',
  'ErrorOther' = 'ErrorOther',
  'ErrorRecognize' = 'ErrorRecognize',
  'ErrorPaketLoss' = 'ErrorPaketLoss',
  'ErrorHangUp' = 'ErrorHangUp',
  'Connected' = 'Connected',
  'RedirectToOperator' = 'RedirectToOperator',
  'CallFinishedNormally' = 'CallFinishedNormally',
  'CallFinishedWithTimeout' = 'CallFinishedWithTimeout',
  'SynchronizationError' = 'SynchronizationError',
  'BotDetected' = 'BotDetected',
}

export const STATE_CALLS: Record<CallsStateType, string> = {
  [CallsStateType.Unknown]: 'Не обработано',
  [CallsStateType.ErrorTimeout]: 'Нет ответа',
  [CallsStateType.ErrorBusy]: 'Номер занят',
  [CallsStateType.ErrorForbidden]: 'Не обработано',
  [CallsStateType.ErrorOther]: 'Прочие ошибки',
  [CallsStateType.ErrorRecognize]: 'Не распознана речь',
  [CallsStateType.ErrorPaketLoss]: 'Потеря пакетов данных',
  [CallsStateType.ErrorHangUp]: 'Досрочное завершение звонка',
  [CallsStateType.Connected]: 'Произведено подключение',
  [CallsStateType.RedirectToOperator]: 'Разговор переведен на оператора',
  [CallsStateType.CallFinishedNormally]:
    'Диалог с абонентом завершен в штатном режиме',
  [CallsStateType.CallFinishedWithTimeout]:
    'Диалог с абонентом не завершен в штатном режиме, модуль RoboCall принудительно завершил обработку',
  [CallsStateType.SynchronizationError]:
    'Ошибка синхронизации между каналом и сокетом',
  [CallsStateType.BotDetected]: 'Обнаружен автоответчик',
};

const CALL_ATTEMPT_BUCKET_LABELS: Partial<Record<CallsStateType, string>> = {
  [CallsStateType.ErrorTimeout]: 'Нет ответа',
  [CallsStateType.ErrorHangUp]: 'Абонент завершил звонок',
  [CallsStateType.CallFinishedNormally]: 'Разговор завершен штатно',
  [CallsStateType.BotDetected]: 'Автоответчик',
  [CallsStateType.ErrorBusy]: 'Ошибка',
  [CallsStateType.ErrorForbidden]: 'Ошибка',
  [CallsStateType.ErrorOther]: 'Ошибка',
  [CallsStateType.ErrorRecognize]: 'Ошибка',
  [CallsStateType.ErrorPaketLoss]: 'Ошибка',
  [CallsStateType.Connected]: 'Ошибка',
  [CallsStateType.RedirectToOperator]: 'Ошибка',
  [CallsStateType.CallFinishedWithTimeout]: 'Ошибка',
  [CallsStateType.SynchronizationError]: 'Ошибка',
  [CallsStateType.Unknown]: 'Ошибка',
};

const ATTEMPT_STATE_LABELS: Record<string, string> = {
  [AttemptState.Processing]: 'В обработке',
  [AttemptState.Normally]: 'Разговор завершен штатно',
  [AttemptState.HangUp]: 'Абонент завершил звонок',
  [AttemptState.NoAnswer]: 'Нет ответа',
  [AttemptState.BotDetected]: 'Автоответчик',
  [AttemptState.Error]: 'Ошибка',
  processing: 'В обработке',
  normally: 'Разговор завершен штатно',
  hang_up: 'Абонент завершил звонок',
  no_answer: 'Нет ответа',
  bot_detected: 'Автоответчик',
  error: 'Ошибка',
};

export interface TaskStatusInfo {
  label: string;
  color: string;
}

export const TASK_STATUS_CONFIG: Record<string, TaskStatusInfo> = {
  // Task processing statuses (shared across all journals)
  NotProcessed: { label: 'Не обработано', color: '#8C8C8C' },
  not_processed: { label: 'Не обработано', color: '#8C8C8C' },
  InProcessing: { label: 'В обработке', color: '#49A4D8' },
  in_processing: { label: 'В обработке', color: '#49A4D8' },
  Processed: { label: 'Обработано', color: '#6FC067' },
  processed: { label: 'Обработано', color: '#6FC067' },
  // Outgoing call results
  Unknown: { label: 'Не обработано', color: '#8C8C8C' },
  NotIdentified: { label: 'Не идентифицирован', color: '#EFA05A' },
  Confirmed: { label: 'Подтверждено', color: '#6FC067' },
  Declined: { label: 'Отменено пациентом', color: '#E24343' },
  Rescheduled: { label: 'Перенесено пациентом', color: '#2D64DD' },
  ClinicCancelled: { label: 'Отменено клиникой', color: '#B9315C' },
  ClinicRescheduled: { label: 'Перенесено клиникой', color: '#4B9CA5' },
  InvalidNumber: { label: 'Невалидный номер', color: '#A12F2F' },
  NeedOperator: { label: 'Требуется оператор', color: '#D0BC0E' },
  BotDetected: { label: 'Автоответчик', color: '#4A4141' },
  // Incoming call results
  Unprocessed: { label: 'Не обработан результат диалога', color: '#8C8C8C' },
  HangUp: { label: 'Досрочное завершение звонка', color: '#2D64DD' },
  ErrorService: { label: 'Ошибка сервиса', color: '#EFA05A' },
  PacketLoss: { label: 'Потеря пакетов', color: '#EFA05A' },
  Success: { label: 'Успешная запись на приём', color: '#6FC067' },
  NotRecognized: { label: 'Не распознана речь', color: '#EFA05A' },
  CallCompletedNormal: {
    label: 'Разговор завершён в штатном режиме',
    color: '#6FC067',
  },
  Operator: { label: 'Требуется оператор', color: '#D0BC0E' },
  // Incoming call stages (StatusTypes)
  InvalidSpeech: { label: 'Не корректный ответ абонента', color: '#EFA05A' },
  Greeting: { label: 'Приветствие', color: '#8C8C8C' },
  AskReason: { label: 'Вопрос о намерении', color: '#8C8C8C' },
  SearchPatient: { label: 'Поиск пациента в МИС', color: '#8C8C8C' },
  AskBirth: { label: 'Вопрос о дне рождения', color: '#8C8C8C' },
  AskFio: { label: 'Вопрос о ФИО', color: '#8C8C8C' },
  NotFoundPatient: { label: 'Пациент не найден в МИС', color: '#EFA05A' },
  AskSpecialization: {
    label: 'Вопрос о специальности врача',
    color: '#8C8C8C',
  },
  NotFoundSpecialization: {
    label: 'Не найдена специализация врача',
    color: '#EFA05A',
  },
  SearchLpu: { label: 'Поиск медицинской организации', color: '#8C8C8C' },
  NotFoundLpu: {
    label: 'Не найдена медицинская организация',
    color: '#EFA05A',
  },
  AskDoctor: { label: 'Вопрос о враче', color: '#8C8C8C' },
  SearchDoctor: { label: 'Поиск врача', color: '#8C8C8C' },
  NotFoundDoctor: { label: 'Не найден врач', color: '#EFA05A' },
  CheckDoubleBooking: { label: 'Проверка двойной брони', color: '#8C8C8C' },
  PatientAlreadyToDoctor: {
    label: 'Пациент уже записан к врачу',
    color: '#EFA05A',
  },
  SearchSlots: { label: 'Поиск свободных талонов', color: '#8C8C8C' },
  NotFoundFreeSlots: { label: 'Не найдены свободные талоны', color: '#EFA05A' },
  AskDateSlot: { label: 'Вопрос о дате талона', color: '#8C8C8C' },
  AskTimeSlot: { label: 'Вопрос о времени талона', color: '#8C8C8C' },
  TryTakeSlot: { label: 'Пробуем забронировать талон', color: '#8C8C8C' },
  CantTakeSlot: { label: 'Не можем забронировать талон', color: '#EFA05A' },
  ReserveSuccess: { label: 'Успешная бронь талона', color: '#6FC067' },
  InvalidSpecialization: {
    label: 'Не можем записать по данной специальности',
    color: '#EFA05A',
  },
  Wait: { label: 'Ожидание', color: '#8C8C8C' },
  AskDocument: { label: 'Вопрос про документ', color: '#8C8C8C' },
  SearchSpecialization: {
    label: 'Поиск специализации врача',
    color: '#8C8C8C',
  },
  MoveSlot: { label: 'Перенос талона', color: '#2D64DD' },
  CancelSlot: { label: 'Отмена талона', color: '#E24343' },
  Goodbye: { label: 'Завершение диалога', color: '#6FC067' },
  BirthConfirm: { label: 'Подтверждаем дату рождения', color: '#8C8C8C' },
  SearchDoctors: { label: 'Поиск врачей', color: '#8C8C8C' },
  SearchSlotsForEach: {
    label: 'Итеративный поиск свободных талонов',
    color: '#8C8C8C',
  },
  LpuConfirm: { label: 'Подтверждаем прикрепление', color: '#8C8C8C' },
  AskOperator: { label: 'Вопрос о переводе на оператора', color: '#D0BC0E' },
  AskCancelSlot: { label: 'Вопрос об отмене талона', color: '#8C8C8C' },
  AskReserve: {
    label: 'Запрос о подтверждении бронирования талона',
    color: '#8C8C8C',
  },
  SpecializationConfirm: {
    label: 'Подтверждаем специализацию',
    color: '#8C8C8C',
  },
  SpecializationInfo: {
    label: 'Информирование о возможности записи через терапевта',
    color: '#8C8C8C',
  },
  AskConcreteDate: { label: 'Спрашиваем нужную дату', color: '#8C8C8C' },
  NotFoundSlot: { label: 'Не найдена конкретная дата', color: '#EFA05A' },
  CallCenterSchedule: { label: 'Расписание колл-центра', color: '#8C8C8C' },
  NotFoundValidClinic: {
    label: 'Не нашли подходящую МО',
    color: '#EFA05A',
  },
  AskCanSpeak: { label: 'Вопрос о продолжении разговора', color: '#8C8C8C' },
  OperatorNotAvailable: { label: 'Оператор недоступен', color: '#E24343' },
  FioConfirm: { label: 'Подтверждение ФИО', color: '#8C8C8C' },
  AskCertainSlot: { label: 'Вопрос о конкретном талоне', color: '#8C8C8C' },
};

export const callAttemptStateLabel = (value?: string | null): string => {
  if (!value) {
    return 'Неизвестно';
  }

  return (
    CALL_ATTEMPT_BUCKET_LABELS[value as CallsStateType] ??
    STATE_CALLS[value as CallsStateType] ??
    ATTEMPT_STATE_LABELS[value] ??
    TASK_STATUS_CONFIG[value]?.label ??
    value
  );
};

export const ERROR_STATES = [
  'Обработка не начата',
  'Нет ответа',
  'Номер занят',
  'Номер запрещен',
  'Прочие ошибки',
  'Потеря пакетов данных',
  'Ошибка синхронизации между каналом и сокетом',
  'Произведено подключение',
];

export type CallAttempt = {
  id: string;
  tenantId: string | null;
  callDateTime: string;
  beginCallDateTime: string;
  endCallDateTime: string;
  callAttemptState: CallsStateType | string | null;
  state?: string | null;
  phone: string;
  recording: string | null;
  tasks: unknown[] | null;
};

export type DialogSpeaker = 'Robot' | 'Speaker1';

export type DialogTranscription = {
  id: string;
  nodeKey: string;
  speaker: DialogSpeaker;
  value: string | null;
  dateTime: string;
};
